<script setup>
// Publicar › Mensagens (Fase 5, tela 07, spec §4.8): o fluxo vertical — quando um cliente começa uma conversa
// (inboxes com toggle e rollout por inbox, conectar/desconectar como antes) → o Polaris se apresenta (saudação,
// config.greeting) → segue o Roteiro (resumo read-only com link) → se não resolver (handoff, link para
// Configurações) → quem e quando (edição segue nas Configurações). Ativar em N inboxes / Pausar sem desconectar.
// Prévia lateral estática: a saudação real + o primeiro turno.
import { computed, nextTick, ref, watch } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useAdmin } from 'dashboard/composables/useAdmin';

import DeleteDialog from 'dashboard/components-next/polaris/pageComponents/DeleteDialog.vue';
import PageLayout from 'dashboard/components-next/polaris/PageLayout.vue';
import ConnectInboxDialog from 'dashboard/components-next/polaris/pageComponents/inbox/ConnectInboxDialog.vue';
import InboxCardSkin from 'dashboard/components-next/polaris/skin/InboxCardSkin.vue';
import InboxPageEmptyState from 'dashboard/components-next/polaris/pageComponents/emptyStates/InboxPageEmptyState.vue';
import { Button } from 'dashboard/components-next/polaris/ui/button';
import { Input } from 'dashboard/components-next/polaris/ui/input';
import { Textarea } from 'dashboard/components-next/polaris/ui/textarea';
import { Switch } from 'dashboard/components-next/polaris/ui/switch';

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const { isAdmin } = useAdmin();

const dialogType = ref('');

const assistantId = computed(() => Number(route.params.assistantId));
const assistantUiFlags = useMapGetter('polarisAssistants/getUIFlags');
const uiFlags = useMapGetter('polarisInboxes/getUIFlags');
const isFetchingAssistant = computed(() => assistantUiFlags.value.fetchingItem);
const isFetching = computed(() => uiFlags.value.fetchingList);

const polarisInboxes = useMapGetter('polarisInboxes/getRecords');
const assistant = computed(() =>
  store.getters['polarisAssistants/getRecord'](assistantId.value)
);
const scenarios = useMapGetter('polarisScenarios/getRecords');
const attributes = useMapGetter('attributes/getContactAttributes');
const collectCount = computed(
  () => (attributes.value || []).filter(item => item.polarisCollect).length
);
const outcomesCount = ref(0);

const activeCount = computed(
  () => polarisInboxes.value.filter(inbox => inbox.active !== false).length
);
const greeting = ref('');
const handoffMessage = computed(
  () => assistant.value?.config?.handoff_message || ''
);

watch(
  assistant,
  record => {
    greeting.value = record?.config?.greeting || '';
  },
  { immediate: true }
);

const saveGreeting = async () => {
  try {
    const config = {
      ...(assistant.value?.config || {}),
      greeting: greeting.value,
    };
    await store.dispatch('polarisAssistants/update', {
      id: assistantId.value,
      config,
    });
    useAlert(t('POLARIS.DEPLOY_MESSAGING.GREETING_SAVED'));
  } catch {
    useAlert(t('POLARIS.DEPLOY_MESSAGING.API_ERROR'));
  }
};

const toggleInbox = async (inbox, active) => {
  try {
    await store.dispatch('polarisInboxes/update', {
      assistantId: assistantId.value,
      inboxId: inbox.id,
      active,
    });
  } catch {
    useAlert(t('POLARIS.DEPLOY_MESSAGING.API_ERROR'));
  }
};

const setRollout = async (inbox, value) => {
  const rollout = Number(value);
  if (!Number.isInteger(rollout) || rollout < 1 || rollout > 100) return;
  try {
    await store.dispatch('polarisInboxes/update', {
      assistantId: assistantId.value,
      inboxId: inbox.id,
      rollout_percentage: rollout,
    });
  } catch {
    useAlert(t('POLARIS.DEPLOY_MESSAGING.API_ERROR'));
  }
};

const setAll = async active => {
  await Promise.all(
    polarisInboxes.value.map(inbox =>
      store.dispatch('polarisInboxes/update', {
        assistantId: assistantId.value,
        inboxId: inbox.id,
        active,
      })
    )
  );
};

const selectedInbox = ref(null);
const disconnectInboxDialog = ref(null);

const handleDelete = () => {
  disconnectInboxDialog.value.dialogRef.open();
};

const connectInboxDialog = ref(null);

const handleCreate = () => {
  dialogType.value = 'create';
  nextTick(() => connectInboxDialog.value.dialogRef.open());
};
const handleAction = ({ action, id }) => {
  selectedInbox.value = polarisInboxes.value.find(inbox => id === inbox.id);
  nextTick(() => {
    if (action === 'delete') {
      handleDelete();
    }
  });
};

const handleCreateClose = () => {
  dialogType.value = '';
  selectedInbox.value = null;
};

const loadOutcomes = async () => {
  try {
    const { default: PolarisOutcomesAPI } = await import(
      'dashboard/api/polaris/outcomes'
    );
    const { data } = await PolarisOutcomesAPI.get({
      assistantId: assistantId.value,
    });
    outcomesCount.value = data.payload.length;
  } catch {
    outcomesCount.value = 0;
  }
};

watch(
  assistantId,
  newId => {
    store.dispatch('polarisInboxes/get', {
      assistantId: newId,
    });
    store.dispatch('polarisAssistants/show', newId);
    store.dispatch('polarisScenarios/get', { assistantId: newId });
    store.dispatch('attributes/get');
    loadOutcomes();
  },
  { immediate: true }
);
</script>

<template>
  <PageLayout
    :header-title="$t('POLARIS.DEPLOY_MESSAGING.TITLE')"
    :is-fetching="isFetchingAssistant || isFetching"
    :show-pagination-footer="false"
    :show-know-more="false"
    :feature-flag="FEATURE_FLAGS.POLARIS"
  >
    <template #headerActions>
      <Button
        v-if="isAdmin && polarisInboxes.length"
        variant="outline"
        size="sm"
        @click="setAll(activeCount < polarisInboxes.length)"
      >
        {{
          activeCount === polarisInboxes.length
            ? $t('POLARIS.DEPLOY_MESSAGING.PAUSE_ALL')
            : $t('POLARIS.DEPLOY_MESSAGING.ACTIVATE_ALL', {
                count: polarisInboxes.length,
              })
        }}
      </Button>
      <Button v-if="isAdmin" size="sm" @click="handleCreate">
        {{ $t('POLARIS.DEPLOY_MESSAGING.CONNECT') }}
      </Button>
    </template>
    <template #body>
      <div class="flex flex-col gap-8">
        <section class="flex flex-col gap-3">
          <h2 class="m-0 text-sm font-semibold text-polaris-foreground">
            {{ $t('POLARIS.DEPLOY_MESSAGING.STEPS.CONVERSATION') }}
          </h2>
          <div v-if="polarisInboxes.length" class="flex flex-col gap-3">
            <div
              v-for="inbox in polarisInboxes"
              :key="inbox.id"
              class="flex items-center gap-3 rounded-polaris-lg border border-solid border-polaris-border bg-polaris-card px-4 py-3"
            >
              <InboxCardSkin
                class="min-w-0 flex-1"
                :inbox="inbox"
                @action="handleAction"
              />
              <label
                class="flex shrink-0 items-center gap-2 text-xs text-polaris-muted-foreground"
              >
                {{ $t('POLARIS.DEPLOY_MESSAGING.ROLLOUT') }}
                <Input
                  class="w-16"
                  type="number"
                  min="1"
                  max="100"
                  :model-value="String(inbox.rollout_percentage ?? 100)"
                  :disabled="!isAdmin"
                  @change="event => setRollout(inbox, event.target.value)"
                />
                %
              </label>
              <Switch
                :model-value="inbox.active !== false"
                :disabled="!isAdmin"
                :aria-label="
                  $t('POLARIS.DEPLOY_MESSAGING.TOGGLE', { name: inbox.name })
                "
                @update:model-value="value => toggleInbox(inbox, value)"
              />
            </div>
          </div>
          <InboxPageEmptyState v-else @click="handleCreate" />
        </section>

        <section class="flex flex-col gap-2">
          <h2 class="m-0 text-sm font-semibold text-polaris-foreground">
            {{ $t('POLARIS.DEPLOY_MESSAGING.STEPS.GREETING') }}
          </h2>
          <Textarea
            v-model="greeting"
            rows="2"
            :disabled="!isAdmin"
            :placeholder="$t('POLARIS.DEPLOY_MESSAGING.GREETING_PLACEHOLDER')"
          />
          <div v-if="isAdmin" class="flex justify-end">
            <Button
              variant="outline"
              size="sm"
              :disabled="greeting === (assistant?.config?.greeting || '')"
              @click="saveGreeting"
            >
              {{ $t('POLARIS.DEPLOY_MESSAGING.SAVE_GREETING') }}
            </Button>
          </div>
        </section>

        <section class="flex flex-col gap-2">
          <h2 class="m-0 text-sm font-semibold text-polaris-foreground">
            {{ $t('POLARIS.DEPLOY_MESSAGING.STEPS.PLAYBOOK') }}
          </h2>
          <p class="m-0 text-sm text-polaris-muted-foreground">
            {{
              $t('POLARIS.DEPLOY_MESSAGING.PLAYBOOK_SUMMARY', {
                scenarios: scenarios.length,
                data: collectCount,
                outcomes: outcomesCount,
              })
            }}
            <RouterLink
              :to="{
                name: 'polaris_assistants_scenarios_index',
                params: {
                  accountId: route.params.accountId,
                  assistantId,
                },
              }"
              class="font-medium text-polaris-info-foreground hover:underline"
            >
              {{ $t('POLARIS.DEPLOY_MESSAGING.PLAYBOOK_LINK') }}
            </RouterLink>
          </p>
        </section>

        <section class="flex flex-col gap-2">
          <h2 class="m-0 text-sm font-semibold text-polaris-foreground">
            {{ $t('POLARIS.DEPLOY_MESSAGING.STEPS.HANDOFF') }}
          </h2>
          <p class="m-0 text-sm text-polaris-muted-foreground">
            {{ handoffMessage || $t('POLARIS.DEPLOY_MESSAGING.HANDOFF_EMPTY') }}
            <RouterLink
              :to="{
                name: 'polaris_assistants_settings_index',
                params: {
                  accountId: route.params.accountId,
                  assistantId,
                },
              }"
              class="font-medium text-polaris-info-foreground hover:underline"
            >
              {{ $t('POLARIS.DEPLOY_MESSAGING.SETTINGS_LINK') }}
            </RouterLink>
          </p>
        </section>

        <section class="flex flex-col gap-2">
          <h2 class="m-0 text-sm font-semibold text-polaris-foreground">
            {{ $t('POLARIS.DEPLOY_MESSAGING.STEPS.AUDIENCE') }}
          </h2>
          <p class="m-0 text-sm text-polaris-muted-foreground">
            {{ $t('POLARIS.DEPLOY_MESSAGING.AUDIENCE_TEXT') }}
            <RouterLink
              :to="{
                name: 'polaris_assistants_settings_index',
                params: {
                  accountId: route.params.accountId,
                  assistantId,
                },
              }"
              class="font-medium text-polaris-info-foreground hover:underline"
            >
              {{ $t('POLARIS.DEPLOY_MESSAGING.SETTINGS_LINK') }}
            </RouterLink>
          </p>
        </section>
      </div>
    </template>

    <template #aside>
      <div
        class="hidden h-full w-96 shrink-0 flex-col border-0 border-l border-solid border-polaris-border bg-polaris-card lg:flex"
      >
        <div
          class="border-0 border-b border-solid border-polaris-border px-4 py-3"
        >
          <h2 class="m-0 text-sm font-semibold text-polaris-foreground">
            {{ $t('POLARIS.DEPLOY_MESSAGING.PREVIEW.TITLE') }}
          </h2>
        </div>
        <div class="flex flex-col gap-3 px-4 py-4">
          <div
            class="max-w-[85%] self-start rounded-polaris-lg bg-polaris-accent px-3 py-2 text-sm text-polaris-foreground"
          >
            {{
              greeting ||
              $t('POLARIS.DEPLOY_MESSAGING.PREVIEW.GREETING_PLACEHOLDER')
            }}
          </div>
          <div
            class="max-w-[85%] self-end rounded-polaris-lg bg-polaris-muted px-3 py-2 text-sm text-polaris-muted-foreground"
          >
            {{ $t('POLARIS.DEPLOY_MESSAGING.PREVIEW.CUSTOMER_TURN') }}
          </div>
        </div>
      </div>
    </template>

    <ConnectInboxDialog
      v-if="dialogType"
      ref="connectInboxDialog"
      :assistant-id="assistantId"
      :type="dialogType"
      @close="handleCreateClose"
    />
    <DeleteDialog
      v-if="selectedInbox"
      ref="disconnectInboxDialog"
      :entity="selectedInbox"
      :delete-payload="{
        assistantId: assistantId,
        inboxId: selectedInbox.id,
      }"
      type="Inboxes"
    />
  </PageLayout>
</template>
